import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { SiteFilterQuery, SiteRegister } from "../../api/sites";
import { Pill } from "../../components/ui";
import { useDebounced } from "../../hooks/useDebounced";
import { isFiltered, isToggleOn, TOGGLES } from "./siteFilter";

type Field = keyof Omit<SiteFilterQuery, "status">;

/** The criteria "More filters" holds. They were all on the old register and
 * links still carry them, so they stay reachable; they are simply not what
 * somebody reaches for first. */
const MORE: readonly Field[] = ["contract_id", "region_id", "valve_make", "dn_min", "dn_max", "work_order_type"];

/** Criteria no control on the toolbar shows -- a link from the tracker or the
 * routing screen can still set them -- listed as chips so a narrowed list
 * always says what narrowed it. */
const HIDDEN_CHIPS: Record<string, (filter: SiteFilterQuery) => boolean> = {
  site_type: () => true,
  located: (filter) => filter.located !== "",
  qcp: (filter) => filter.has_qcp !== "",
  assigned: (filter) => filter.assigned === "assigned",
};

/** Search, cluster and provider, the quick toggles with their counts, and the
 * rarer criteria behind "More filters". Every change is a new filter, which the
 * page writes to the URL. */
export default function RegisterToolbar({
  filter,
  register,
  onChange,
  onSearch,
  onClear,
}: {
  filter: SiteFilterQuery;
  register: SiteRegister | undefined;
  onChange: (field: Field, value: string) => void;
  onSearch: (q: string) => void;
  onClear: () => void;
}) {
  const moreActive = MORE.some((field) => filter[field] !== "");
  const [moreOpen, setMoreOpen] = useState(moreActive);
  const filters = register?.filters;
  const hiddenChips = (register?.chips ?? []).filter((chip) => HIDDEN_CHIPS[chip.field]?.(filter) ?? false);

  return (
    <div className="site-toolbar">
      <div className="toolbar">
        <SearchBox value={filter.q} onSearch={onSearch} />
        <select
          className="select"
          aria-label="Cluster"
          value={filter.cluster_id}
          onChange={(e) => onChange("cluster_id", e.target.value)}
        >
          <option value="">All clusters</option>
          {(filters?.clusters ?? [])
            .filter((c) => filter.region_id === "" || String(c.region_id) === filter.region_id)
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
        </select>
        <select
          className="select"
          aria-label="Provider"
          value={filter.provider_id}
          onChange={(e) => onChange("provider_id", e.target.value)}
        >
          <option value="">All providers</option>
          {(filters?.providers ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <span className="toolbar-rule" aria-hidden="true" />
        <div className="pill-row" role="group" aria-label="Quick filters">
          {TOGGLES.map((toggle) => {
            const on = isToggleOn(filter, toggle);
            return (
              <Pill
                key={toggle.key}
                on={on}
                count={register?.toggle_counts[toggle.key]}
                onToggle={() => onChange(toggle.field, on ? "" : toggle.value)}
              >
                {toggle.label}
              </Pill>
            );
          })}
        </div>
        <button
          type="button"
          className="go-link"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen(!moreOpen)}
        >
          {moreOpen ? "Fewer filters" : moreActive ? "More filters (on)" : "More filters"}
        </button>
        {isFiltered(filter) ? (
          <button type="button" className="go-link" onClick={onClear}>
            Clear all
          </button>
        ) : null}
      </div>

      {moreOpen ? (
        <div className="toolbar site-more-filters">
          <label className="field">
            Contract
            <select value={filter.contract_id} onChange={(e) => onChange("contract_id", e.target.value)}>
              <option value="">All</option>
              {(filters?.contracts ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            Region
            <select value={filter.region_id} onChange={(e) => onChange("region_id", e.target.value)}>
              <option value="">All</option>
              {(filters?.regions ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
          <TextFilter label="Valve make" value={filter.valve_make} onCommit={(v) => onChange("valve_make", v)} />
          <TextFilter label="DN from" value={filter.dn_min} numeric onCommit={(v) => onChange("dn_min", v)} />
          <TextFilter label="DN to" value={filter.dn_max} numeric onCommit={(v) => onChange("dn_max", v)} />
          <label className="field">
            Work order type
            <select value={filter.work_order_type} onChange={(e) => onChange("work_order_type", e.target.value)}>
              <option value="">Any</option>
              <option value="PM41">PM41</option>
              <option value="PM42">PM42</option>
              {/* The jobs the scheduling gate would hold as PM42 by default. */}
              <option value="unset">Not stated</option>
            </select>
          </label>
        </div>
      ) : null}

      {hiddenChips.length > 0 ? (
        <div className="pill-row" aria-label="Other filters">
          {hiddenChips.map((chip) => (
            <Link key={`${chip.field}-${chip.value}`} className="pill" aria-pressed="true" to={`/sites?${chip.remove_query}`}>
              {chip.label}: {chip.value} <span aria-hidden="true">×</span>
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** The search box. What is typed shows at once; the register is asked once
 * the typing pauses. A search that arrives from outside -- the masthead sends
 * Enter to `/sites?q=…` -- replaces what is in the box, but the box's own
 * write coming back through the URL does not, or it would eat the keystrokes
 * typed while that write was on its way. */
function SearchBox({ value, onSearch }: { value: string; onSearch: (q: string) => void }) {
  const [text, setText] = useState(value);
  const written = useRef(value);
  const settled = useDebounced(text);

  useEffect(() => {
    if (value !== written.current) {
      written.current = value;
      setText(value);
    }
  }, [value]);

  useEffect(() => {
    if (settled !== written.current) {
      written.current = settled;
      onSearch(settled);
    }
    // Only a settled change writes; `onSearch` is a fresh closure each render
    // and is deliberately not a dependency.
  }, [settled]);

  return (
    <input
      type="search"
      className="search-input"
      aria-label="Search sites"
      placeholder="Search name, address or works order"
      value={text}
      onChange={(e) => setText(e.target.value)}
    />
  );
}

/** A free-text criterion that filters when you leave the box or press Enter,
 * not on every keystroke. */
function TextFilter({
  label,
  value,
  numeric = false,
  onCommit,
}: {
  label: string;
  value: string;
  numeric?: boolean;
  onCommit: (value: string) => void;
}) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const commit = () => {
    if (text.trim() !== value) {
      onCommit(text.trim());
    }
  };
  return (
    <label className="field">
      {label}
      <input
        value={text}
        inputMode={numeric ? "numeric" : undefined}
        className={numeric ? "site-num-input" : undefined}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            commit();
          }
        }}
      />
    </label>
  );
}
